// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color } from "@stanion/XDL/Utilities";
import React, { FC } from "react";
import styled from "styled-components";

export interface CutEdgeProps {
    borderWidth?: string;
    edgeSize?: string;
    customWidth?: string;
    borderColor?: string;
    backgroundColor?: string;
    padding?: string;
    oneCuttingEdge?: boolean;
}

type Props = CutEdgeProps;

const CutEdge = styled.div<{
    backgroundColor?: string;
    borderColor?: string;
    padding?: string;
    edgeSize?: string;
    borderWidth?: string;
    customWidth?: string;
}>`
    background-color: ${props => color(props.backgroundColor)};
    width: ${props => props.customWidth};
    padding: ${props => props.padding};
    border: 0;
    position: relative;
    isolation: isolate;
    margin-top: 8px;
    display: inline-block;
    clip-path: polygon(
        ${props => props.edgeSize} 0%,
        100% 0,
        100% calc(100% - ${props => props.edgeSize}),
        calc(100% - ${props => props.edgeSize}) 100%,
        0 100%,
        0% ${props => props.edgeSize}
    );

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        background: ${props => color(props.borderColor)};
        z-index: -2;
    }

    &::after {
        content: "";
        position: absolute;
        inset: 0;
        background: ${props => color(props.backgroundColor)};
        z-index: -1;
        clip-path: polygon(
            ${props => props.borderWidth} calc(${props => props.edgeSize} + ${props => props.borderWidth} * 0.5),
            calc(${props => props.edgeSize} + ${props => props.borderWidth} * 0.5) ${props => props.borderWidth},
            calc(100% - ${props => props.borderWidth}) ${props => props.borderWidth},
            calc(100% - ${props => props.borderWidth})
                calc(100% - calc(${props => props.edgeSize} + ${props => props.borderWidth} * 0.5)),
            calc(100% - calc(${props => props.edgeSize} + ${props => props.borderWidth} * 0.5))
                calc(100% - ${props => props.borderWidth}),
            calc(${props => props.borderWidth}) calc(100% - ${props => props.borderWidth})
        );
    }
`;

const OneCutEdge = styled.div<{
    backgroundColor?: string;
    borderColor?: string;
    padding?: string;
    edgeSize?: string;
    borderWidth?: string;
    customWidth?: string;
}>`
    background-color: ${props => color(props.backgroundColor)};
    width: ${props => props.customWidth};
    padding: ${props => props.padding};
    border: 0;
    position: relative;
    isolation: isolate;
    margin-top: 8px;
    display: inline-block;
    > span:last-child {
        position: absolute;
        left: ${props => props.borderWidth};
        bottom: ${props => props.borderWidth};
        right: ${props => props.borderWidth};
        height: 6px;
        background-color: ${color("secondary")};
        z-index: 1;
    }
    clip-path: polygon(
        ${props => props.edgeSize} 0%,
        100% 0,
        100% calc(100% - ${props => props.edgeSize}),
        100% 100%,
        0 100%,
        0% ${props => props.edgeSize}
    );

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        background: ${props => color(props.borderColor)};
        z-index: -2;
    }

    &::after {
        content: "";
        position: absolute;
        inset: 0;
        background: ${props => color(props.backgroundColor)};
        z-index: -1;
        clip-path: polygon(
            ${props => props.borderWidth} calc(${props => props.edgeSize} + ${props => props.borderWidth} * 0.5),
            calc(${props => props.edgeSize} + ${props => props.borderWidth} * 0.5) ${props => props.borderWidth},
            calc(100% - ${props => props.borderWidth}) ${props => props.borderWidth},
            calc(100% - ${props => props.borderWidth}) 100%,
            100% calc(100% - ${props => props.borderWidth}),
            calc(${props => props.borderWidth}) calc(100% - ${props => props.borderWidth})
        );
    }
`;

const CutEdgeContainer: FC<Props> = ({
    children,
    edgeSize,
    borderWidth,
    customWidth,
    borderColor,
    backgroundColor,
    padding,
    oneCuttingEdge = false,
}) => {
    return (
        <>
            {!oneCuttingEdge && (
                <CutEdge
                    edgeSize={edgeSize || "8px"}
                    borderWidth={borderWidth || "1px"}
                    borderColor={borderColor || "primary"}
                    backgroundColor={backgroundColor || "secondary"}
                    customWidth={customWidth || "auto"}
                    padding={padding || "5px 9px"}
                >
                    {children}
                </CutEdge>
            )}
            {oneCuttingEdge && (
                <OneCutEdge
                    edgeSize={edgeSize || "8px"}
                    borderWidth={borderWidth || "1px"}
                    borderColor={borderColor || "primary"}
                    backgroundColor={backgroundColor || "secondary"}
                    customWidth={customWidth || "auto"}
                    padding={padding || "5px 9px"}
                >
                    {children}
                    <span></span>
                </OneCutEdge>
            )}
        </>
    );
};

export default CutEdgeContainer;
